/**
 * BUNKER 84 — enemies (v0.1).
 *
 * v0.1 ships ONE enemy type ("guard"): standing patrol in their spawn
 * tile, fires every ~45 frames when they have line-of-sight to the
 * player, dies in one hit. Real sprite billboards + multi-state AI
 * land in v0.2 (sc-4542 / sc-4543); for v0.1 the renderer draws a
 * tall colored rectangle per live enemy so the player has something
 * to shoot.
 *
 * Spawn list is hand-tuned for level1 — placed in rooms the player
 * has to enter and at corridor T-junctions. Guard count is
 * deliberately modest (5) so a full run takes ~60-90 seconds, the
 * v0.1 target.
 */

import { isSolid, tileAt } from './tiles.js';
import type { Player } from './player.js';

/** Enemy state. v0.1 has no patrol/alert distinction; v0.2.3 expands. */
export type EnemyState = 'alive' | 'dying' | 'dead';

export interface Enemy {
  /** Tile-space position (center of a tile by convention). */
  x: number;
  y: number;
  state: EnemyState;
  /** Tick this enemy first acquired LOS to the player. -1 = never. */
  acquiredTick: number;
  /** Tick this enemy last shot the player. Used for fire cadence. */
  lastShotTick: number;
  /** Tick this enemy entered 'dying' state. v0.1 deletes after a short delay. */
  diedAtTick: number;
}

/**
 * Frames between consecutive guard shots when LOS is held. ~1.5 sec
 * at 60Hz. Slow enough that a player with 3 HP has time to aim + fire
 * back at least twice before dying to a single guard.
 */
export const GUARD_FIRE_COOLDOWN = 90;
/**
 * Frames a guard waits after FIRST acquiring the player before they
 * start shooting. ~1 sec heads-up so the player isn't dead the moment
 * they round a corner. Real "alert" animation lands in sc-4543.
 */
export const GUARD_AGGRO_DELAY = 60;
/** Frames a dying enemy stays on screen before removal. */
export const DEATH_LINGER_FRAMES = 30;
/** Enemy bounding-radius along ray (tile units). Hitscan hits if ray passes within. */
export const ENEMY_HIT_RADIUS = 0.35;
/** Max range a guard's hitscan reaches. Tile units. */
export const GUARD_SIGHT_TILES = 10;
/** Enemy "logical height" — billboard rectangle is this tall in tile units. */
export const ENEMY_HEIGHT_TILES = 0.85;

/**
 * Initial spawn list for level1. Coordinates are tile-space centers
 * (.5 offsets), placed in actual open tiles + reachable from spawn.
 *
 * **No enemy in direct LOS from spawn** — playtest pass on sc-4538
 * showed that spawning the player face-to-face with a guard makes
 * the cartridge punishing. All 5 spawns are positioned BEHIND wall
 * geometry (central pillar, room corners) so the player has to walk
 * out of the spawn corridor + turn to find the first target. First
 * encounter happens after ~5 seconds of forward motion.
 *
 * v0.1 has no door mechanics, so the NW/NE rooms behind the row-11
 * door wall and the locked sub-room are intentionally unreachable
 * until v0.3 — no enemies go there yet.
 */
export function makeLevel1Enemies(): Enemy[] {
  const spawns: ReadonlyArray<readonly [number, number]> = [
    [13, 21],  // first encounter — west of central pillar, hidden by corner
    [22, 17],  // central junction NE — second encounter after turning
    [10, 13],  // NW main level — exploration reward
    [16, 13],  // far north center — exploration reward
    [25, 14],  // east main level — exploration reward
  ];
  return spawns.map(([tx, ty]) => ({
    x: tx + 0.5,
    y: ty + 0.5,
    state: 'alive' as EnemyState,
    acquiredTick: -1, // not yet aware of player
    lastShotTick: -GUARD_FIRE_COOLDOWN,
    diedAtTick: 0,
  }));
}

/**
 * True if there's a clear line of sight from (ax, ay) to (bx, by) — no
 * solid tile between. Uses the same DDA stepping as the wall raycaster
 * but stops at the first solid OR at the target. ~32 step budget is
 * way more than any in-level LOS query needs.
 */
export function hasLineOfSight(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  map: Uint8Array
): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 0.001) return true;
  const stepCount = Math.ceil(dist * 4); // 4 samples per tile — generous
  const stepX = dx / stepCount;
  const stepY = dy / stepCount;
  let x = ax;
  let y = ay;
  // Skip step 0 (the source tile itself) and the final step (target tile).
  for (let i = 1; i < stepCount; i++) {
    x += stepX;
    y += stepY;
    if (isSolid(tileAt(map, Math.floor(x), Math.floor(y)))) {
      return false;
    }
  }
  return true;
}

/**
 * Cast a hitscan ray from `player` along `(dirX, dirY)` (unit vector)
 * and return the FIRST live enemy hit if any, plus the distance to
 * the wall along the same ray.
 *
 * Caller filters: an enemy is "hit" only if `enemyDist < wallDist` AND
 * the ray passes within ENEMY_HIT_RADIUS of the enemy center.
 *
 * Ray-circle intersection: project (enemy - player) onto dir to find
 * the closest-approach distance along the ray; check the perpendicular
 * miss distance against ENEMY_HIT_RADIUS.
 */
export interface HitscanResult {
  /** Index into the enemies array, or -1 if none. */
  enemyIndex: number;
  /** Distance from player to enemy along the ray. */
  enemyDist: number;
  /** Distance from player to wall along the ray. */
  wallDist: number;
}

/**
 * Walk the wall DDA inline so we don't import from the raycaster's
 * column-specific castColumn (which expects camera-x parameters). This
 * is the same algorithm just framed around an arbitrary unit-direction
 * ray.
 */
function castWallAlongRay(
  px: number,
  py: number,
  rayDirX: number,
  rayDirY: number,
  map: Uint8Array
): number {
  let mapX = Math.floor(px);
  let mapY = Math.floor(py);
  const deltaDistX = rayDirX === 0 ? 1e30 : Math.abs(1 / rayDirX);
  const deltaDistY = rayDirY === 0 ? 1e30 : Math.abs(1 / rayDirY);
  let stepX: number;
  let sideDistX: number;
  if (rayDirX < 0) {
    stepX = -1;
    sideDistX = (px - mapX) * deltaDistX;
  } else {
    stepX = 1;
    sideDistX = (mapX + 1 - px) * deltaDistX;
  }
  let stepY: number;
  let sideDistY: number;
  if (rayDirY < 0) {
    stepY = -1;
    sideDistY = (py - mapY) * deltaDistY;
  } else {
    stepY = 1;
    sideDistY = (mapY + 1 - py) * deltaDistY;
  }
  let side: 'x' | 'y' = 'x';
  for (let i = 0; i < 96; i++) {
    if (sideDistX < sideDistY) {
      sideDistX += deltaDistX;
      mapX += stepX;
      side = 'x';
    } else {
      sideDistY += deltaDistY;
      mapY += stepY;
      side = 'y';
    }
    if (isSolid(tileAt(map, mapX, mapY))) {
      return side === 'x' ? sideDistX - deltaDistX : sideDistY - deltaDistY;
    }
  }
  return 1e30;
}

export function castHitscan(
  player: Player,
  dirX: number,
  dirY: number,
  enemies: readonly Enemy[],
  map: Uint8Array
): HitscanResult {
  const wallDist = castWallAlongRay(player.x, player.y, dirX, dirY, map);

  let bestIdx = -1;
  let bestDist = wallDist;
  for (let i = 0; i < enemies.length; i++) {
    const e = enemies[i]!;
    if (e.state !== 'alive') continue;
    // Vector from player to enemy.
    const vx = e.x - player.x;
    const vy = e.y - player.y;
    // Project onto ray direction. dot = how far along the ray the enemy is.
    const along = vx * dirX + vy * dirY;
    if (along <= 0) continue; // behind player
    if (along >= bestDist) continue; // a closer hit already exists
    // Perpendicular distance from ray to enemy center.
    const perpX = vx - along * dirX;
    const perpY = vy - along * dirY;
    const perpDist2 = perpX * perpX + perpY * perpY;
    if (perpDist2 > ENEMY_HIT_RADIUS * ENEMY_HIT_RADIUS) continue;
    bestIdx = i;
    bestDist = along;
  }

  return { enemyIndex: bestIdx, enemyDist: bestDist, wallDist };
}
